import React, { useCallback, useEffect, useState } from 'react';
import { ArrowButton } from 'view/molecules/PaginationComponents';
import { doubleLeft, doubleRight, left, right } from 'static/icons/resizable';
import {
  computeConfig,
  createContentPart,
  createNavigateManager,
  createSpecialPart,
} from './utils/utils';
import { CONFIG_PAGINATION, IPaginationProps } from './Pagination.model';
import * as S from './Pagination.styles';

export const Pagination = ({
  countPages,
  currentPage,
  changeCurrentPage,
  dontScrollOnChange,
}: IPaginationProps) => {
  const [config, setConfig] = useState<CONFIG_PAGINATION>(
    CONFIG_PAGINATION.first,
  );
  const { goToFirst, goToLast, goToNext, goToPrev } = createNavigateManager({
    countPages,
    currentPage,
    changeCurrentPage,
  });
  const [mapSpecialShove, setMapSpecialShove] = useState({
    right: false,
    left: false,
  });

  const configIsNotIn = (
    ...paginationConfigKeys: (keyof typeof CONFIG_PAGINATION)[]
  ) =>
    !paginationConfigKeys.some((key) => {
      return CONFIG_PAGINATION[config] === key;
    });

  useEffect(() => {
    setMapSpecialShove({
      left: configIsNotIn('second', 'third', 'first'),
      right: configIsNotIn('fifth', 'sixth', 'first'),
    });
  }, [config]);

  useEffect(() => {
    computeConfig({ countPages, currentPage, setConfig });
  }, [countPages, currentPage]);

  useEffect(() => {
    if (!dontScrollOnChange) {
      document.getElementById('root')?.children[0].scroll(0, 0);
    }
  }, [currentPage]);

  const Content = useCallback(
    () =>
      createContentPart({ countPages, currentPage, changeCurrentPage, config }),
    [countPages, currentPage, config],
  );

  const Special = useCallback(
    () =>
      createSpecialPart({ countPages, currentPage, changeCurrentPage, config }),
    [countPages, config],
  );

  return (
    <S.PaginationWrap>
      <S.PaginationArrowBoxLeft>
        {countPages > 5 && (
          <ArrowButton
            src={doubleLeft}
            onClick={goToFirst}
            disabled={currentPage === 1}
            data-testid='button-arrow-double-left'
          />
        )}
        <ArrowButton
          src={left}
          onClick={goToPrev}
          disabled={currentPage === 1}
          data-testid='button-arrow-left'
        />
      </S.PaginationArrowBoxLeft>

      <S.PaginationItemBox>
        {mapSpecialShove.left && <Special />}
        <Content />
        {mapSpecialShove.right && <Special />}
      </S.PaginationItemBox>

      <S.PaginationArrowBoxRight>
        <ArrowButton
          src={right}
          onClick={goToNext}
          disabled={currentPage === countPages}
          data-testid='button-arrow-right'
        />
        {countPages > 5 && (
          <ArrowButton
            src={doubleRight}
            onClick={goToLast}
            disabled={currentPage === countPages}
            data-testid='button-arrow-double-right'
          />
        )}
      </S.PaginationArrowBoxRight>
    </S.PaginationWrap>
  );
};
